import React, { useEffect, useRef, useState } from 'react';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { LoadingIcon, CustomLoadingIcon } from '../../supplementary_components/loading_icon';
import { addNotification, addPopup } from '../../supplementary_components/store';
import { can } from '../../supplementary_components/userDataDelegate';
import { priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { ConfirmationDialogue } from '../../general_components/forms/ConfirmationDialogue';
import { CalendarPicker, useCloseOnOutsidePress } from '../../general_components/content_management/reusableItems/calendar';
import { localDateToUSFormat } from '../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection';
import { purchaseTypeLabel } from './purchaseTypeOptions';
import styles from './equipmentPaymentsReport.module.scss';

const TABS = [
    { id: 'LEASED', label: 'Leased / Leased To Own' },
    { id: 'FINANCED', label: 'Financed' },
    { id: 'PAID_OFF', label: 'Paid Off' },
    { id: 'OTHER', label: 'Other' },
];

const COLUMN_WIDTH = {
    toggle: '2.5rem',
    number: '1.1fr',
    status: '0.9fr',
    type: '1.3fr',
    start: '1fr',
    end: '1fr',
    monthly: '1.15fr',
    remaining: '1.35fr',
    total: '1.05fr',
    lessor: '1.15fr',
};

function columnsForTab(tab) {
    const base = [
        { key: 'toggle', label: '' },
        { key: 'number', label: 'Unit Number' },
        { key: 'status', label: 'Status' },
        { key: 'type', label: 'Purchase Type' },
    ];
    const start = { key: 'start', label: 'Start Date' };
    const end = { key: 'end', label: 'End Date' };
    const monthly = { key: 'monthly', label: 'Monthly Payment' };
    const remaining = { key: 'remaining', label: 'Remaining Balance' };
    const total = { key: 'total', label: 'Total Amount' };
    const lessor = { key: 'lessor', label: 'Lessor' };
    if (tab === 'OTHER') return [...base, end, lessor];
    if (tab === 'FINANCED') return [...base, start, monthly, remaining, total];
    if (tab === 'PAID_OFF') return [...base, start, end, monthly, total, lessor];
    return [...base, start, end, monthly, lessor];
}

function compareUnits(a, b, section) {
    if (section === 'LEASED') {
        const rank = (type) => (type === 'LEASED_TO_OWN' ? 0 : 1);
        const byType = rank(a.purchaseType) - rank(b.purchaseType);
        if (byType !== 0) return byType;
    }
    return String(a.number).localeCompare(String(b.number), undefined, { numeric: true, sensitivity: 'base' });
}

function toIsoDate(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}
const RECENT_MONTHS = 6;

function monthLabel(year, month) {
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${names[month - 1]} ${year}`;
}

function money(value) {
    if (value == null || value === '') return '-';
    return priceOnFinish(value);
}

function cents(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return 0;
    return Math.round(amount * 100);
}

function remainingBalance(unit) {
    if (unit.paymentTotalAmount == null || unit.paymentTotalAmount === '') return null;
    return (cents(unit.paymentTotalAmount) - cents(unit.paidTotal)) / 100;
}

function request(method, url, body) {
    return new Promise((resolve, reject) => {
        sendRequest(method, url, null, resolve, () => reject(new Error('request failed')), body);
    });
}

function confirmRemoval() {
    return new Promise((resolve) => {
        let settled = false;
        const finish = (accepted) => {
            if (settled) return;
            settled = true;
            resolve(accepted);
        };
        addPopup({
            content: ConfirmationDialogue,
            popupData: {
                formTitle: 'Update payments',
                name: 'confirmEquipmentPaymentChange',
                message: 'Payments are going to be edited and some data may be removed. Do you wish to proceed?',
                submitTitle: 'Proceed',
                action: (done) => {
                    finish(true);
                    done(true);
                },
                cancelAction: (close) => {
                    finish(false);
                    close();
                },
            },
            EZCloseAllowed: false,
        });
    });
}

export function EquipmentPaymentsReport({ equipment }) {
    const trucks = equipment === 'trucks';
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('LEASED');
    const [openId, setOpenId] = useState(null);
    const canEdit = can('MONTHLY_REPORTS', 'MANAGE');

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        sendRequest(
            'GET',
            `${API_ROOT}equipment-payments?equipment=${equipment}`,
            controller.signal,
            (res) => {
                setReport(res);
                setLoading(false);
            },
            () => {
                setLoading(false);
                addNotification({ message: 'Could not load payments', type: 'alert' });
            },
        );
        return () => controller.abort();
    }, [equipment]);

    const units = [...(report?.sections?.[tab] || [])].sort((a, b) => compareUnits(a, b, tab));
    const showMonthlyTotal = tab === 'LEASED' || tab === 'FINANCED';
    const monthlyTotal = units.reduce((sum, unit) => {
        const amount = Number(unit.monthlyPayment);
        return Number.isFinite(amount) ? sum + Math.round(amount * 100) : sum;
    }, 0) / 100;
    const columns = columnsForTab(tab);
    const gridStyle = { gridTemplateColumns: columns.map((column) => COLUMN_WIDTH[column.key]).join(' ') };

    const replaceUnit = (next) => {
        setReport((current) => {
            if (!current?.sections || !next) return current;
            const sections = {};
            Object.entries(current.sections).forEach(([key, list]) => {
                sections[key] = list.filter((unit) => unit.id !== next.id);
            });
            const section = next.section || tab;
            sections[section] = [...(sections[section] || []), next].sort((a, b) => compareUnits(a, b, section));
            return { ...current, sections };
        });
    };

    return (
        <div className={styles.page}>
            <div className="pageHeader"><div>{trucks ? 'Truck Payments' : 'Trailer Payments'}</div></div>
            <div className={styles.tabs}>
                <div className={styles.tabList}>
                    {TABS.map((item) => (
                        <button key={item.id} type="button" className={tab === item.id ? styles.selected : ''} onClick={() => setTab(item.id)}>
                            {item.label}
                        </button>
                    ))}
                </div>
                {showMonthlyTotal ? (
                    <div className={styles.tabTotal}><span className={styles.tabTotalLabel}>Total Monthly Payment:</span> {money(monthlyTotal)}</div>
                ) : null}
            </div>
            {loading ? <LoadingIcon state height="8rem" /> : (
                <div className={styles.list}>
                    <div className={`${styles.grid} ${styles.head}`} style={gridStyle}>
                        {columns.map((column) => (
                            <div key={column.key}>{column.label}</div>
                        ))}
                    </div>
                    {units.length === 0 ? (
                        <div className={styles.empty}>No {trucks ? 'trucks' : 'trailers'} in this section.</div>
                    ) : units.map((unit) => (
                        <UnitRow
                            key={unit.id}
                            unit={unit}
                            trucks={trucks}
                            columns={columns}
                            gridStyle={gridStyle}
                            open={openId === unit.id}
                            today={report.today}
                            canEdit={canEdit}
                            onToggle={() => setOpenId((current) => (current === unit.id ? null : unit.id))}
                            onUpdated={replaceUnit}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function UnitRow({ unit, trucks, columns, gridStyle, open, today, canEdit, onToggle, onUpdated }) {
    const type = unit.purchaseType;
    const leased = type === 'LEASE' || type === 'LEASED_TO_OWN';
    const financed = type === 'FINANCED';
    const returned = type === 'LEASE' && unit.paymentEndDate && today && unit.paymentEndDate < today;
    const cells = {
        toggle: <button type="button" className={styles.toggle} onClick={onToggle}>{open ? '▾' : '▸'}</button>,
        number: <button type="button" className={styles.number} onClick={onToggle}>{unit.number}</button>,
        status: <StatusBadge status={unit.status} />,
        type: <span>{returned ? 'Returned' : purchaseTypeLabel(unit.purchaseType)}</span>,
        start: <TermField field="paymentStartDate" value={unit.paymentStartDate} kind="date" editable={canEdit && (leased || financed)} trucks={trucks} unit={unit} onUpdated={onUpdated} />,
        end: <TermField field="paymentEndDate" value={unit.paymentEndDate} kind="date" editable={canEdit && leased} trucks={trucks} unit={unit} onUpdated={onUpdated} />,
        monthly: <TermField field="monthlyPayment" value={unit.monthlyPayment} kind="money" editable={canEdit && (leased || financed)} trucks={trucks} unit={unit} onUpdated={onUpdated} />,
        remaining: <RemainingBalance unit={unit} editable={canEdit && financed} trucks={trucks} onUpdated={onUpdated} />,
        total: <TermField field="paymentTotalAmount" value={unit.paymentTotalAmount} kind="money" editable={canEdit && financed} trucks={trucks} unit={unit} onUpdated={onUpdated} />,
        lessor: <span className={styles.muted}>{unit.lessorName || '-'}</span>,
    };

    return (
        <div className={`${styles.row} ${open ? styles.rowOpen : ''}`}>
            <div className={styles.grid} style={gridStyle}>
                {columns.map((column) => <div key={column.key}>{cells[column.key]}</div>)}
            </div>
            {open ? (
                <div className={styles.details}>
                    <PaymentHistory unit={unit} canEdit={canEdit} onUpdated={onUpdated} />
                </div>
            ) : null}
        </div>
    );
}

function StatusBadge({ status }) {
    if (status === 'Active') return <span className={`${styles.badge} ${styles.active}`}>Active</span>;
    if (status === 'Sold') return <span className={`${styles.badge} ${styles.sold}`}>Sold</span>;
    if (status === 'Terminated' || status === 'Inactive') return <span className={`${styles.badge} ${styles.terminated}`}>{status}</span>;
    return <span className={styles.muted}>{status}</span>;
}

function TermField({ field, value, kind, editable, trucks, unit, onUpdated }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const shown = kind === 'money' ? money(value) : (value ? localDateToUSFormat(value) : '-');

    const commit = async (nextDraft) => {
        const next = kind === 'money'
            ? (String(nextDraft).trim() === '' ? null : Number(String(nextDraft).replace(/[^0-9.]/g, '')))
            : (nextDraft || null);
        const previous = value ?? null;
        if (next === previous || (kind === 'money' && Number(next) === Number(previous))) {
            setEditing(false);
            return;
        }
        if (kind === 'money' && next != null && Number.isNaN(next)) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            let saved = await request('PATCH', `${API_ROOT}equipment-payments/terms`, {
                equipment: trucks ? 'trucks' : 'trailers',
                id: unit.id,
                [field]: next,
                confirmRemoval: false,
            });
            if (saved?.confirmationRequired) {
                const accepted = await confirmRemoval();
                if (!accepted) {
                    setEditing(false);
                    setSaving(false);
                    return;
                }
                saved = await request('PATCH', `${API_ROOT}equipment-payments/terms`, {
                    equipment: trucks ? 'trucks' : 'trailers',
                    id: unit.id,
                    [field]: next,
                    confirmRemoval: true,
                });
            }
            onUpdated(saved);
            setEditing(false);
        } catch (error) {
            addNotification({ message: 'Could not save', type: 'alert' });
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    if (!editable) {
        return <span className={styles.muted}>{shown}</span>;
    }
    if (kind === 'date') {
        return <DateCell value={value} saving={saving} display={shown} onCommit={commit} />;
    }
    return (
        <span className={styles.cell} onClick={(event) => event.stopPropagation()}>
            {saving ? <CustomLoadingIcon height="1rem" /> : editing ? (
                <input
                    className={styles.inlineInput}
                    autoFocus
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onBlur={() => commit(draft)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') event.currentTarget.blur();
                        if (event.key === 'Escape') setEditing(false);
                    }}
                />
            ) : (
                <button
                    type="button"
                    className={styles.tag}
                    onClick={() => {
                        setDraft(value ?? '');
                        setEditing(true);
                    }}
                >
                    {shown}
                </button>
            )}
        </span>
    );
}

function RemainingBalance({ unit, editable, trucks, onUpdated }) {
    const balance = remainingBalance(unit);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const shown = money(balance);

    const commit = async (nextDraft) => {
        const parsed = String(nextDraft).trim() === '' ? null : Number(String(nextDraft).replace(/[^0-9.]/g, ''));
        if (parsed == null || Number.isNaN(parsed) || parsed < 0 || balance == null) {
            setEditing(false);
            return;
        }
        const nextCents = Math.round(parsed * 100);
        if (nextCents === cents(balance)) {
            setEditing(false);
            return;
        }
        const paidCents = cents(unit.paidTotal);
        if (nextCents === 0 && paidCents <= 0) {
            addNotification({ message: 'Record a payment before marking this unit paid off', type: 'alert' });
            setEditing(false);
            return;
        }
        const nextTotal = (paidCents + nextCents) / 100;
        setSaving(true);
        try {
            const body = {
                equipment: trucks ? 'trucks' : 'trailers',
                id: unit.id,
                paymentTotalAmount: nextTotal,
            };
            let saved = await request('PATCH', `${API_ROOT}equipment-payments/terms`, { ...body, confirmRemoval: false });
            if (saved?.confirmationRequired) {
                const accepted = await confirmRemoval();
                if (!accepted) {
                    setEditing(false);
                    setSaving(false);
                    return;
                }
                saved = await request('PATCH', `${API_ROOT}equipment-payments/terms`, { ...body, confirmRemoval: true });
            }
            onUpdated(saved);
            setEditing(false);
        } catch (error) {
            addNotification({ message: 'Could not save', type: 'alert' });
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    if (!editable || balance == null) {
        return <span className={styles.muted}>{shown}</span>;
    }
    return (
        <span className={styles.cell} onClick={(event) => event.stopPropagation()}>
            {saving ? <CustomLoadingIcon height="1rem" /> : editing ? (
                <input
                    className={styles.inlineInput}
                    autoFocus
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onBlur={() => commit(draft)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') event.currentTarget.blur();
                        if (event.key === 'Escape') setEditing(false);
                    }}
                />
            ) : (
                <button
                    type="button"
                    className={styles.tag}
                    onClick={() => {
                        setDraft(balance.toFixed(2));
                        setEditing(true);
                    }}
                >
                    {shown}
                </button>
            )}
        </span>
    );
}

function DateCell({ value, saving, display, onCommit }) {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const popoverRef = useRef(null);
    useCloseOnOutsidePress(open, [anchorRef, popoverRef], () => setOpen(false));
    const initialStart = value ? new Date(`${value}T00:00:00`) : undefined;
    return (
        <span className={styles.cell} ref={anchorRef} onClick={(event) => event.stopPropagation()}>
            {saving ? <CustomLoadingIcon height="1rem" /> : (
                <button type="button" className={styles.tag} onClick={() => setOpen(true)}>{display}</button>
            )}
            {open ? (
                <CalendarPicker
                    selectType="singleDate"
                    anchorRef={anchorRef}
                    popoverRef={popoverRef}
                    popoverAlign="start"
                    initialStart={initialStart}
                    onCancel={() => setOpen(false)}
                    onApply={(picked) => {
                        setOpen(false);
                        onCommit(toIsoDate(picked));
                    }}
                />
            ) : null}
        </span>
    );
}

function PaymentHistory({ unit, canEdit, onUpdated }) {
    const [showAll, setShowAll] = useState(false);
    const payments = unit.payments || [];
    const hidden = Math.max(0, payments.length - RECENT_MONTHS);
    const visible = showAll || hidden === 0 ? payments : payments.slice(payments.length - RECENT_MONTHS);
    return (
        <div className={styles.historyPanel}>
            {payments.length === 0 ? (
                <div className={styles.noHistory}>No payment history</div>
            ) : (
                <>
                    {hidden > 0 && !showAll ? (
                        <div className={styles.historyBar}>
                            <button type="button" className={styles.more} onClick={() => setShowAll(true)}>
                                Show {hidden} earlier {hidden === 1 ? 'month' : 'months'}
                            </button>
                        </div>
                    ) : null}
                    <div className={styles.historyHead}>
                        <span>Month</span>
                        <span>Amount</span>
                        <span>Note</span>
                    </div>
                    {visible.map((payment) => (
                        <PaymentRow key={payment.id || `${payment.year}-${payment.month}`} payment={payment} unit={unit} canEdit={canEdit} onUpdated={onUpdated} />
                    ))}
                </>
            )}
        </div>
    );
}

function PaymentRow({ payment, unit, canEdit, onUpdated }) {
    const save = async (amount, note) => {
        const parsed = Number(String(amount).replace(/[^0-9.]/g, ''));
        if (Number.isNaN(parsed) || parsed < 0) return;
        if (parsed === Number(payment.amount) && (note || '') === (payment.note || '')) return;
        try {
            const saved = await request('PATCH', `${API_ROOT}equipment-payments/${payment.id}`, { amount: parsed, note });
            onUpdated({
                ...unit,
                paidTotal: saved.paidTotal,
                section: saved.section,
                payments: unit.payments.map((row) => (row.id === saved.id ? { ...row, amount: saved.amount, note: saved.note } : row)),
            });
        } catch (error) {
            addNotification({ message: 'Could not save the payment', type: 'alert' });
        }
    };
    return (
        <div className={styles.historyRow}>
            <span className={styles.historyMonth}>{monthLabel(payment.year, payment.month)}</span>
            <span className={styles.historyAmount}>
                <EditableValue
                    value={payment.amount}
                    display={money(payment.amount)}
                    canEdit={canEdit && payment.id}
                    onCommit={(draft) => save(draft, payment.note || '')}
                />
            </span>
            <span className={styles.historyNote}>
                <EditableValue
                    value={payment.note || ''}
                    display={payment.note || '-'}
                    placeholder={!payment.note}
                    canEdit={canEdit && payment.id}
                    onCommit={(draft) => save(payment.amount, draft)}
                />
            </span>
        </div>
    );
}

function EditableValue({ value, display, canEdit, onCommit, align, placeholder }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    if (saving) return <CustomLoadingIcon height="1rem" />;
    if (editing) {
        return (
            <input
                className={`${styles.inlineInput} ${align === 'right' ? styles.alignRight : ''}`}
                autoFocus
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={async () => {
                    setSaving(true);
                    await onCommit(draft);
                    setSaving(false);
                    setEditing(false);
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') event.currentTarget.blur();
                    if (event.key === 'Escape') setEditing(false);
                }}
            />
        );
    }
    return (
        <button
            type="button"
            className={`${styles.tag} ${align === 'right' ? styles.alignRight : ''} ${placeholder ? styles.placeholder : ''}`}
            onClick={() => {
                if (!canEdit) return;
                setDraft(value ?? '');
                setEditing(true);
            }}
        >
            {display}
        </button>
    );
}
